/**
 * FlowChartSkeleton
 *
 * A skeleton loading state for the FlowChart component
 */

import React from 'react'
import { cn } from '@/lib/utils'

interface FlowChartSkeletonProps {
  className?: string
}

export const FlowChartSkeleton: React.FC<FlowChartSkeletonProps> = ({ className }) => {
  return (
    <div
      className={cn(
        'border-border bg-card my-4 flex animate-pulse flex-col overflow-hidden rounded-lg border shadow-sm transition-all',
        className
      )}
    >
      {/* Header */}
      <div className="flex items-center border-b p-3">
        <div className="flex items-center gap-2">
          <div className="size-4 rounded bg-gray-200 dark:bg-gray-700"></div>
          <div className="h-4 w-24 rounded bg-gray-200 dark:bg-gray-700"></div>
        </div>
      </div>

      {/* Flow chart visualization */}
      <div className="p-4">
        <div className="flex h-[300px] w-full items-center justify-center rounded-lg bg-gray-100 dark:bg-gray-800">
          <div className="relative size-full">
            {/* Start node */}
            <div className="absolute left-1/2 top-4 flex h-12 w-32 -translate-x-1/2 items-center justify-center rounded-lg bg-blue-200 dark:bg-blue-900">
              <div className="h-4 w-16 rounded bg-gray-200 dark:bg-gray-700"></div>
            </div>

            {/* Middle nodes */}
            <div className="absolute left-1/4 top-1/3 h-12 w-32 -translate-x-1/2 rounded-lg bg-green-200 dark:bg-green-900"></div>
            <div className="absolute right-1/4 top-1/3 h-12 w-32 translate-x-1/2 rounded-lg bg-green-200 dark:bg-green-900"></div>

            {/* End node */}
            <div className="absolute bottom-4 left-1/2 h-12 w-32 -translate-x-1/2 rounded-lg bg-red-200 dark:bg-red-900"></div>

            {/* Decision node */}
            <div className="absolute left-1/2 top-2/3 size-16 -translate-x-1/2 -translate-y-1/2 rotate-45 rounded-lg bg-yellow-200 dark:bg-yellow-900"></div>

            {/* Connection arrows */}
            <svg className="absolute inset-0 size-full" xmlns="http://www.w3.org/2000/svg">
              {/* Start to middle nodes */}
              <line
                x1="50%"
                y1="16px"
                x2="25%"
                y2="33%"
                stroke="currentColor"
                strokeWidth="2"
                className="text-gray-300 dark:text-gray-600"
              />
              <line
                x1="50%"
                y1="16px"
                x2="75%"
                y2="33%"
                stroke="currentColor"
                strokeWidth="2"
                className="text-gray-300 dark:text-gray-600"
              />

              {/* Middle nodes to decision */}
              <line
                x1="25%"
                y1="33%"
                x2="50%"
                y2="67%"
                stroke="currentColor"
                strokeWidth="2"
                className="text-gray-300 dark:text-gray-600"
              />
              <line
                x1="75%"
                y1="33%"
                x2="50%"
                y2="67%"
                stroke="currentColor"
                strokeWidth="2"
                className="text-gray-300 dark:text-gray-600"
              />

              {/* Decision to end */}
              <line
                x1="50%"
                y1="67%"
                x2="50%"
                y2="calc(100% - 16px)"
                stroke="currentColor"
                strokeWidth="2"
                className="text-gray-300 dark:text-gray-600"
              />
            </svg>
          </div>
        </div>
      </div>

      {/* Controls */}
      <div className="border-t p-3">
        <div className="flex justify-end gap-2">
          <div className="size-8 rounded bg-gray-200 dark:bg-gray-700"></div>
          <div className="size-8 rounded bg-gray-200 dark:bg-gray-700"></div>
        </div>
      </div>
    </div>
  )
}
